/**
 * StudentLayout Component — Soorgaa Design System
 * 
 * Primary student application shell.
 * - Mobile (<1024px): BottomNav primary navigation, glass-overlay sticky header, secondary drawer
 * - Desktop: Floating rounded rail (expanded or icons-only) inside one rounded content container
 * - Accessibility: Skip-to-content link, aria-current on active tabs, keyboard search
 * - Pure architectural layout: Content sits directly on bg-background without artificial card wrapping
 */
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { Skeleton } from "../components/ui/Skeleton";
import {
  Brain,
  LayoutDashboard,
  GraduationCap,
  Settings,
  Star,
  Search,
  ChevronRight,
  Shield,
  Award,
  Menu,
  X,
  Calculator,
  HelpCircle,
  WifiOff,
  Download,
  PanelLeftClose,
  PanelLeftOpen,
  Trophy,
} from "lucide-react";
import { Logo } from "../components/ui/Logo";
import { useAuth } from "../context/AuthContext";
import { useStudent } from "../context/StudentContext";
import { ThemeToggle } from "../context/ThemeContext";
import { NotificationBell } from "../components/student/NotificationBell";
import { Backdrop } from "../components/ui/Backdrop";
import { BottomNav } from "../components/ui/BottomNav";
import { Modal } from "../components/ui/Modal";
import { StatusBanner } from "../components/ui/StatusBanner";
import { usePWAInstall } from "../lib/usePWAInstall";
import { IOSInstallSheet } from "../components/pwa/IOSInstallSheet";
import { api } from "../lib/api";
import { useAttemptSync } from "../lib/useAttemptSync";

const desktopNav = [
  { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/student/study", label: "Study Hub", icon: GraduationCap },
  { to: "/student/quiz", label: "Quizzes", icon: Brain },
  { to: "/student/exams", label: "Past exams", icon: Trophy },
  { to: "/student/coc", label: "COC Exams", icon: Award },
  { to: "/student/gpa", label: "GPA Calculator", icon: Calculator },
  { to: "/student/downloads", label: "Downloads", icon: Download },
];

const secondaryNav = [
  { to: "/student/settings", label: "Settings", icon: Settings },
  { to: "/student/help", label: "Help & Support", icon: HelpCircle },
];

export function StudentLayout() {
  const { isPremium, setIsPremium, openUpgrade, isAdminPreview } = useStudent();
  const { tokens } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [profile, setProfile] = useState<{ name: string; university: string; avatar: string } | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true
  );

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("soorgaa_rail_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((v) => {
      try {
        localStorage.setItem("soorgaa_rail_collapsed", v ? "0" : "1");
      } catch {
        /* preference not saved */
      }
      return !v;
    });

  useAttemptSync();

  const { canInstall, isInstalled, showIOSPrompt, setShowIOSPrompt, install } = usePWAInstall();

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileDrawerOpen(false);
    setIsMobileSearchOpen(false);
  }, [location.pathname]);

  // Pages render only after the plan is known, so premium-only or free-only sections never flash and vanish.
  const [planResolved, setPlanResolved] = useState(false);
  useEffect(() => {
    if (!tokens?.accessToken) return;
    api.profile(tokens.accessToken)
      .then((p) => {
        setProfile({ name: p.fullName, university: p.university ?? "—", avatar: p.avatar });
        if (!isAdminPreview) setIsPremium(p.isPremium);
      })
      .catch(() => {})
      .finally(() => setPlanResolved(true));
  }, [tokens?.accessToken, setIsPremium, isAdminPreview]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    setIsMobileSearchOpen(false);
    navigate(`/student/study?search=${encodeURIComponent(q)}`);
  }

  const title = (() => {
    const path = location.pathname;
    if (path === "/student") return "Dashboard";
    if (path.startsWith("/student/study")) return "Study Hub";
    if (path.startsWith("/student/coc")) return "COC Exams";
    if (path.startsWith("/student/gpa")) return "GPA Calculator";
    if (path.startsWith("/student/downloads")) return "Downloads";
    if (path.startsWith("/student/profile")) return "Profile";
    if (path.startsWith("/student/settings")) return "Settings";
    if (path.startsWith("/student/help")) return "Help & Support";
    return "Soorgaa";
  })();

  const renderSidebar = (compact: boolean) => {
    const itemClass = (isActive: boolean) =>
      `group relative flex h-11 items-center rounded-key text-sm font-semibold transition-colors ${
        compact ? "w-11 justify-center" : "gap-3 px-3"
      } ${isActive ? "bg-accent-soft text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;
    /* Collapsed rail: label pill slides out to the right on hover/focus. */
    const pill = (label: string) =>
      compact ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-x-2 -translate-y-1/2 whitespace-nowrap rounded-key bg-accent-soft px-3 py-2 text-sm font-medium text-primary opacity-0 shadow-lg transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 motion-reduce:transition-none"
        >
          {label}
        </span>
      ) : null;

    return (
      <div className={`h-full bg-card border border-border flex flex-col rounded-rail ${compact ? "items-center" : ""}`}>
        <div className={`h-16 flex items-center ${compact ? "justify-center" : "justify-between px-4"}`}>
          {compact ? <Logo variant="compact" /> : <Logo />}
          <button
            type="button"
            onClick={() => setIsMobileDrawerOpen(false)}
            className="lg:hidden flex h-11 w-11 items-center justify-center rounded-key text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        <nav className={`flex-1 px-3 py-2 space-y-1 ${compact ? "overflow-visible" : "overflow-y-auto"}`} aria-label="Sidebar Navigation">
          {desktopNav.map((item) => (
            <NavLink key={item.label} to={item.to} end={item.end} aria-label={compact ? item.label : undefined} className={({ isActive }) => itemClass(isActive)}>
              <item.icon size={20} className="shrink-0" aria-hidden="true" />
              {!compact && <span className="flex-1">{item.label}</span>}
              {pill(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-3 space-y-1">
          {secondaryNav.map((item) => (
            <NavLink key={item.to} to={item.to} aria-label={compact ? item.label : undefined} className={({ isActive }) => itemClass(isActive)}>
              <item.icon size={20} className="shrink-0" aria-hidden="true" />
              {!compact && <span>{item.label}</span>}
              {pill(item.label)}
            </NavLink>
          ))}

          {canInstall && !isInstalled && (
            <button
              type="button"
              onClick={() => {
                install();
                setIsMobileDrawerOpen(false);
              }}
              aria-label={compact ? "Install app" : undefined}
              className={`${itemClass(false)} w-full cursor-pointer ${compact ? "w-11" : ""}`}
            >
              <Download size={20} className="shrink-0" aria-hidden="true" />
              {!compact && <span>Install app</span>}
              {pill("Install app")}
            </button>
          )}

          {!isPremium && !isAdminPreview && (
            <button
              type="button"
              onClick={() => openUpgrade()}
              aria-label={compact ? "Get premium" : undefined}
              className={`group relative mt-2 flex h-11 items-center rounded-key bg-primary text-sm font-semibold text-on-accent hover:opacity-90 transition cursor-pointer ${
                compact ? "w-11 justify-center" : "w-full gap-2 px-3"
              }`}
            >
              <Star size={18} className="shrink-0" aria-hidden="true" />
              {!compact && <span>Get premium</span>}
              {pill("Get premium")}
            </button>
          )}

          {isAdminPreview && (
            <NavLink to="/admin" aria-label={compact ? "Back to admin" : undefined} className={() => itemClass(false)}>
              <Shield size={20} className="shrink-0" aria-hidden="true" />
              {!compact && <span>Back to admin</span>}
              {pill("Back to admin")}
            </NavLink>
          )}

          <div className={`flex items-center pt-2 ${compact ? "flex-col gap-2" : "justify-between"}`}>
            <NavLink
              to="/student/profile"
              aria-label="Profile"
              className={`group relative flex items-center gap-2.5 rounded-key p-1 hover:bg-secondary min-w-0 ${compact ? "" : "flex-1"}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-primary">
                {profile?.avatar ?? "S"}
              </span>
              {!compact && (
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-foreground">{profile?.name ?? "Student"}</span>
                  <span className="block truncate text-xs text-muted-foreground">{profile?.university ?? "—"}</span>
                </span>
              )}
              {pill("Profile")}
            </NavLink>
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}
              className="hidden lg:flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              {compact ? <PanelLeftOpen size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-svh flex bg-card text-foreground">
      {/* Skip to main content accessibility link */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-on-accent focus:rounded-md focus:font-semibold focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Desktop floating rail: detached, 12px from every edge */}
      <aside className={`fixed top-3 bottom-3 left-3 z-30 hidden lg:block ${collapsed ? "w-20" : "w-60"}`}>
        {renderSidebar(collapsed)}
      </aside>

      {/* Mobile Drawer Backdrop */}
      <Backdrop
        isOpen={isMobileDrawerOpen}
        onClick={() => setIsMobileDrawerOpen(false)}
        className="lg:hidden"
        zIndex="z-40"
      />

      {/* Mobile Drawer Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 p-3 transition-transform duration-200 lg:hidden ${
          isMobileDrawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {renderSidebar(false)}
      </div>

      {/* Main Content Area: one large rounded container on desktop */}
      <div className={`flex-1 flex flex-col min-h-svh w-full min-w-0 lg:p-3 ${collapsed ? "lg:ml-[calc(5rem+0.75rem)]" : "lg:ml-[calc(15rem+0.75rem)]"}`}>
        <div className="flex-1 flex flex-col min-w-0 bg-background lg:rounded-container lg:border lg:border-border">
        {isAdminPreview && (
          <div className="border-b border-primary/20 bg-primary/10 px-4 py-2 text-xs text-foreground flex items-center justify-between gap-3 lg:rounded-t-container">
            <span className="flex items-center gap-2 font-medium">
              <Shield size={14} className="text-primary" />
              Admin preview — full access granted (no premium limits)
            </span>
            <Link to="/admin" className="underline font-semibold shrink-0 text-primary">Admin panel</Link>
          </div>
        )}

        {/* Top bar: soft search and soft icon buttons */}
        <header className="glass-overlay min-h-16 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 sticky top-0 z-20 lg:rounded-t-container">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setIsMobileDrawerOpen(true)}
              className="lg:hidden flex h-11 w-11 items-center justify-center rounded-control bg-soft text-foreground cursor-pointer"
              aria-label="Open navigation menu"
            >
              <Menu size={18} />
            </button>
            <h2 className="text-lg sm:text-xl font-bold text-foreground truncate font-display">{title}</h2>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop inline search */}
            <form onSubmit={handleSearchSubmit} className="relative hidden md:block" role="search">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden="true" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                aria-label="Search"
                className="h-11 w-56 lg:w-72 rounded-control border-0 bg-soft pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </form>

            {/* Mobile search button */}
            <button
              type="button"
              onClick={() => setIsMobileSearchOpen(true)}
              className="md:hidden flex h-11 w-11 items-center justify-center rounded-control bg-soft text-muted-foreground hover:text-foreground cursor-pointer"
              aria-label="Search modules and exams"
            >
              <Search size={18} />
            </button>

            <NotificationBell />
            <ThemeToggle />

            {!isOnline && (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold bg-warning/10 text-warning border border-warning/30 flex items-center gap-1.5 shrink-0"
                title="You are currently working offline"
              >
                <WifiOff size={12} className="animate-pulse" />
                <span className="hidden sm:inline">Offline mode</span>
              </span>
            )}

            <Link
              to="/student/profile"
              className="flex h-11 w-11 items-center justify-center rounded-control bg-soft text-sm font-bold text-primary hover:bg-lift transition-colors"
              aria-label="Student profile"
            >
              {profile?.avatar ?? "S"}
            </Link>
          </div>
        </header>

        {/* Persistent Offline Notification Banner */}
        {!isOnline && (
          <StatusBanner
            type="info"
            message="You're offline. Downloaded notes and your current quiz still work."
            className="rounded-none border-x-0 border-t-0"
          />
        )}

        {/* Mobile Search Sheet */}
        <Modal
          isOpen={isMobileSearchOpen}
          onClose={() => setIsMobileSearchOpen(false)}
          title="Search Soorgaa"
          size="sm"
        >
          <form onSubmit={handleSearchSubmit} className="space-y-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subjects, notes, past exams…"
                className="field pl-9 pr-3 py-2 text-base"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsMobileSearchOpen(false)}
                className="px-4 py-2 text-sm font-semibold rounded-md border border-border hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-on-accent hover:opacity-90"
              >
                Search
              </button>
            </div>
          </form>
        </Modal>

        {/* Main page content — sits directly on background, with bottom padding for mobile BottomNav */}
        <main
          id="main"
          tabIndex={-1}
          className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-8 w-full max-w-7xl mx-auto focus:outline-none"
        >
          {planResolved || isAdminPreview ? (
            <Outlet />
          ) : (
            <div className="space-y-6" aria-busy="true" aria-label="Loading">
              <Skeleton className="h-64 w-full rounded-card" />
              <Skeleton className="h-40 w-full rounded-card" />
            </div>
          )}
        </main>
        </div>
      </div>

      {/* Mobile Primary Bottom Navigation */}
      <BottomNav />

      {/* iOS Safari Home Screen Instructions Sheet */}
      <IOSInstallSheet
        isOpen={showIOSPrompt}
        onClose={() => setShowIOSPrompt(false)}
      />
    </div>
  );
}
